import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { conversationAnalyzerService } from '@/services/practiceService';
import LoadingState from '@/components/ui/LoadingState';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import ConversationAnalyzerForm from './components/ConversationAnalyzerForm';
import AnalysisResults from './components/AnalysisResults';
import AnalysisHistory from './components/AnalysisHistory';
import { Lock } from 'lucide-react';

export default function ConversationAnalyzer() {
  const { user, profile, loading: authLoading } = useAuth();
  const { hasAccess, getUpgradeMessage } = useSubscription();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('new-analysis');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState('');
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadAnalysisHistory();
  }, [user]);

  const loadAnalysisHistory = async () => {
    if (!user) return;
    setIsLoadingHistory(true);
    try {
      const data = await conversationAnalyzerService.getUserAnalyses();
      setAnalysisHistory(data || []);
    } catch (err) {
      console.error('Error loading analysis history:', err);
      setAnalysisHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleAnalyze = async (analysisData) => {
    setIsAnalyzing(true);
    setError('');
    try {
      const result = await conversationAnalyzerService.analyzeConversation({
        ...analysisData,
        industry: analysisData.industry || profile?.industry || 'general'
      });

      // Preserve client text locally for suggested responses
      const enrichedResult = {
        ...result,
        client_text: analysisData?.clientText || null
      };

      setCurrentAnalysis(enrichedResult);
      setActiveTab('results');
      await loadAnalysisHistory();
    } catch (err) {
      setError(err.message || 'Failed to analyze conversation');
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleNewAnalysis = () => {
    setActiveTab('new-analysis');
  };

  const handleViewAnalysis = (analysis) => {
    setCurrentAnalysis(analysis);
    setActiveTab('results');
  };

  const previewFeatures = [
    {
      icon: 'Brain',
      title: 'Elite AI breakdowns',
      description: 'Line-by-line scoring across discovery, objections, and closing with tactical fixes.'
    },
    {
      icon: 'AudioLines',
      title: 'Transcript & call support',
      description: 'Drop in call recordings or pasted text; we handle diarization, tagging, and highlights.'
    },
    {
      icon: 'Sparkles',
      title: 'Next-step coaching',
      description: 'Concrete rewrites, objection rebuttals, and follow-up guidance tailored to your industry.'
    }
  ];

  if (authLoading) {
    return <LoadingState />;
  }

  const canAccess = hasAccess('conversationAnalyzer');

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      
      <main className="max-w-7xl mx-auto px-4 py-8 mb-20">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Conversation Analyzer</h1>
          <p className="text-gray-600">
            Upload call transcripts or email threads to get framework-based feedback
          </p>
        </div>

        {/* Premium Feature Lock */}
        {!canAccess && (
          <div className="mb-10 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 shadow-lg">
            <div className="flex flex-col lg:flex-row">
              <div className="flex-1 p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
                    <Icon name="Crown" size={14} />
                    Elite
                  </span>
                  <span className="text-xs text-indigo-700">Conversation Analyzer</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Turn any call into coaching-grade insight
                </h2>
                <p className="text-gray-700 mb-6 max-w-2xl">
                  Upload recordings or transcripts and get elite-level scoring, coaching prompts, and battle-tested rebuttals so you can close the next deal faster.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {previewFeatures.map((feature) => (
                    <div key={feature.title} className="rounded-xl border border-blue-100 bg-white/60 p-4 shadow-sm">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center mb-3">
                        <Icon name={feature.icon} size={18} />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button 
                    onClick={() => navigate('/app/subscription')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    Upgrade to Elite
                  </Button>
                  <p className="text-sm text-indigo-800">
                    Elite unlocks Conversation Analyzer + AI Roleplay + Lumo Progress.
                  </p>
                </div>
              </div>
              <div className="flex-1 bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-8 lg:p-10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-indigo-100">
                    <Lock className="w-4 h-4" />
                    Premium Preview
                  </div>
                  <p className="text-2xl font-semibold mt-3 mb-4 leading-tight">
                    Sample feedback you'll see after upgrading
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="p-4 rounded-xl bg-white/10 border border-white/20">
                      <p className="text-indigo-100 font-semibold mb-1">Discovery clarity: 82 → 94</p>
                      <p className="text-indigo-50">“Great rapport building. Ask a budget anchor before sharing pricing to keep control.”</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/10 border border-white/20">
                      <p className="text-indigo-100 font-semibold mb-1">Objection rebuttal ready</p>
                      <p className="text-indigo-50">“When they say ‘we're happy with our current vendor’, try: ‘What would make them 10/10 for you?’”</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/10 border border-white/20">
                      <p className="text-indigo-100 font-semibold mb-1">Follow-up crafted</p>
                      <p className="text-indigo-50">Email draft + call agenda auto-generated from your transcript.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-xs text-indigo-100">
                  Elite members also get industry-specific scorecards, advanced call tagging, and download-ready summaries.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-destructive font-medium">Error</p>
                <p className="text-sm text-destructive/80">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        {canAccess && (
          <>
            <div className="flex gap-2 mb-8 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('new-analysis')}
                className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'new-analysis'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                New Analysis
              </button>
              <button
                onClick={() => setActiveTab('results')}
                disabled={!currentAnalysis}
                className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'results'
                    ? 'border-blue-600 text-blue-600'
                    : currentAnalysis
                    ? 'border-transparent text-gray-600 hover:text-gray-900'
                    : 'border-transparent text-gray-400'
                }`}
              >
                Results
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'history'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                History ({analysisHistory.length})
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              {activeTab === 'new-analysis' && (
                <ConversationAnalyzerForm 
                  onAnalyze={handleAnalyze}
                  isLoading={isAnalyzing}
                />
              )}

              {activeTab === 'results' && currentAnalysis && (
                <AnalysisResults 
                  analysis={currentAnalysis}
                  onNewAnalysis={handleNewAnalysis}
                />
              )}

              {activeTab === 'history' && (
                <AnalysisHistory 
                  analyses={analysisHistory}
                  onSelectAnalysis={handleViewAnalysis}
                  isLoading={isLoadingHistory}
                />
              )}
            </div>
          </>
        )}
      </main></div>
  );
}
